/**
 * Custom article: sticky sidebar offset + copy-link share buttons.
 */
(function () {
  const LOG_PREFIX = '';

  class CustomArticle extends HTMLElement {
    #resizeObserver;

    connectedCallback() {
      this.#bindCopyButtons();
      this.#updateStickyOffset();
      window.addEventListener('resize', this.#onResize, { passive: true });
      window.addEventListener('scroll', this.#onResize, { passive: true });

      const header = document.querySelector('header-component, .header, #header-group, .shopify-section-header-sticky');
      if (header && typeof ResizeObserver !== 'undefined') {
        this.#resizeObserver = new ResizeObserver(() => this.#updateStickyOffset());
        this.#resizeObserver.observe(header);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.#onResize);
      window.removeEventListener('scroll', this.#onResize);
      this.#resizeObserver?.disconnect();
      this.querySelectorAll('.custom-article__share-btn--copy[data-copy-url]').forEach((button) => {
        const timerId = this.#copyResetTimers.get(button);
        if (timerId) {
          window.clearTimeout(timerId);
        }
      });
    }

    #onResize = () => {
      this.#updateStickyOffset();
    };

    #updateStickyOffset() {
      const stickyEnabled = this.dataset.stickySidebar === 'true';
      if (!stickyEnabled) return;

      const header = document.querySelector(
        'header-component, .header-section, .header, #header-group, .shopify-section-group-header-group'
      );
      let headerHeight = 0;

      if (header) {
        const rect = header.getBoundingClientRect();
        headerHeight = rect.bottom > 0 ? Math.ceil(rect.bottom) : Math.ceil(rect.height);
      }

      const baseOffset = Number.parseInt(this.dataset.stickyOffset || '24', 10) || 24;
      const top = headerHeight + baseOffset;

      this.style.setProperty('--custom-article-sticky-top', `${top}px`);
    }

    #copyResetTimers = new WeakMap();

    #bindCopyButtons() {
      const buttons = this.querySelectorAll('.custom-article__share-btn--copy[data-copy-url]');
      buttons.forEach((button) => {
        button.addEventListener('click', async () => {
          const url = button.getAttribute('data-copy-url');
          if (!url) return;

          const feedbackMs =
            Number.parseInt(button.dataset.copyFeedbackMs || '5000', 10) || 5000;
          const copiedLabel = button.dataset.copiedLabel || 'Link copied';
          const labelEl = button.querySelector('.custom-article__share-copy-label');
          const originalAriaLabel = button.getAttribute('aria-label');
          const originalVisibleLabel = labelEl?.textContent?.trim() || '';

          const resetCopyFeedback = () => {
            button.classList.remove('is-copied');
            if (originalAriaLabel) {
              button.setAttribute('aria-label', originalAriaLabel);
            }
            if (labelEl && originalVisibleLabel) {
              labelEl.textContent = originalVisibleLabel;
            }
            this.#copyResetTimers.delete(button);
          };

          const existingTimer = this.#copyResetTimers.get(button);
          if (existingTimer) {
            window.clearTimeout(existingTimer);
          }

          try {
            await navigator.clipboard.writeText(url);
            button.classList.add('is-copied');
            button.setAttribute('aria-label', copiedLabel);
            if (labelEl) {
              labelEl.textContent = copiedLabel;
            }

            const timerId = window.setTimeout(resetCopyFeedback, feedbackMs);
            this.#copyResetTimers.set(button, timerId);
          } catch (error) {
            console.log(LOG_PREFIX, 'Copy link failed', error);
          }
        });
      });
    }
  }

  if (!customElements.get('custom-article')) {
    customElements.define('custom-article', CustomArticle);
  }
})();
